import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { useAuth } from '../../hooks/useAuth';
import { AcceptInviteForm } from './AcceptInviteForm';
import { InviteHeader, InviteShell, SignInLink } from './AcceptInviteLayout';
import { useInvitationPreview } from './useInvitationPreview';
import type { AcceptInviteOutcome } from './useInvitationPreview';

/**
 * The invited coach's first screen: confirm whose invitation this is, set a password, done.
 *
 * **The token arrives in the URL fragment, not the query string, and this page clears it on sight.**
 * `index.html` loads Google Fonts on every route, so a token in the query string would be handed to
 * a third party in a `Referer` header on the invitee's very first paint, and would then sit in the
 * address bar and in history. A fragment is never sent in `Referer` and never reaches an access log.
 * Read it with `window.location.hash`; `useSearchParams` would find nothing.
 *
 * **Acceptance issues no session.** The API creates the account and stops there, so this ends on an
 * explicit hand-off to sign-in rather than trying to log anybody in.
 */

/**
 * The token out of `#token=...`.
 *
 * Returns null for a fragment that is missing, empty or carries something else, which lands on the
 * same invalid-or-expired state as a token the server rejects. A link with no token is not a
 * different problem from a link with a dead one, as far as the person holding it is concerned.
 */
const readTokenFromHash = (): string | null => {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;

  const token = new URLSearchParams(hash).get('token')?.trim();
  return token || null;
};

/** Drops the fragment without adding a history entry, so the token stops being visible or reachable. */
const clearHash = () => {
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}`
  );
};

export const AcceptInvite = () => {
  const { user, logout } = useAuth();

  /**
   * Read during the first render, before the effect below can clear it. Reading the fragment is
   * idempotent, so a double-invoked initializer is harmless; clearing it is not, which is why that
   * half lives in an effect.
   */
  const [token] = useState(readTokenFromHash);
  const [outcome, setOutcome] = useState<AcceptInviteOutcome | null>(null);
  const { status, reload } = useInvitationPreview(token);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    clearHash();
  }, []);

  /**
   * Signing out anyone already logged in, as ResetPassword does for the same reason: the success
   * state hands off to `/login`, and that route bounces a signed-in user to `/players`, so an
   * invitee who happened to be signed in as somebody else would finish onboarding in a dead end.
   *
   * Routed through AuthContext rather than `services/auth` directly, because only the context
   * version also clears the cached team chips, column layouts and schedule scope that must not leak
   * across tenants. This page is the likeliest place in the app for two different people to use one
   * browser, which is exactly what that cleanup was written for.
   *
   * Runs once, on mount: `AppRoutes` renders no route until auth has settled, so `user` is already
   * final here, and re-running on it would fight the sign-out it just performed.
   */
  useEffect(() => {
    if (user) {
      void logout().catch(() => {
        // A failed sign-out must not take the invitation down with it. The account still gets
        // created; the stale session is the signed-in user's own problem to retry.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** A submission result replaces the preview outright rather than coexisting with it. */
  const view = outcome ?? status;

  /**
   * Every phase swaps the whole page, so the focused control unmounts and focus would fall to
   * `<body>` with nothing announced. Moving it to the new heading is the remedy for a change of
   * context, not a change of context itself, and the form is left alone so it can be tabbed normally.
   */
  useEffect(() => {
    if (view.phase !== 'loading' && view.phase !== 'form') {
      headingRef.current?.focus();
    }
  }, [view.phase]);

  if (view.phase === 'loading') {
    return (
      <InviteShell>
        <div className="accept-invite-loading" role="status">
          <p>Checking your invitation...</p>
        </div>
      </InviteShell>
    );
  }

  if (view.phase === 'invalid') {
    return (
      <InviteShell>
        <InviteHeader title="This invitation link no longer works" headingRef={headingRef} />
        <div className="accept-invite-message accept-invite-message--error">
          <p>This invitation is invalid or has expired.</p>
          <p>Ask whoever invited you to send a new one.</p>
        </div>
        <div className="accept-invite-actions">
          <SignInLink>Go to sign in</SignInLink>
        </div>
      </InviteShell>
    );
  }

  if (view.phase === 'paused') {
    return (
      <InviteShell>
        <InviteHeader title="This program is on hold" headingRef={headingRef} />
        <div className="accept-invite-message accept-invite-message--warning">
          <p>{view.message}</p>
          <p>Your invitation still works. Try the link again once the program is active.</p>
        </div>
        <div className="accept-invite-actions">
          <SignInLink>Go to sign in</SignInLink>
        </div>
      </InviteShell>
    );
  }

  if (view.phase === 'unavailable') {
    return (
      <InviteShell>
        <InviteHeader title="We could not check your invitation" headingRef={headingRef} />
        <div className="accept-invite-message accept-invite-message--error">
          <p>{view.message}</p>
        </div>
        <div className="accept-invite-actions">
          {view.canRetry && (
            <Button
              type="button"
              fullWidth
              onClick={() => void reload()}
              data-testid="retry-button"
            >
              Try again
            </Button>
          )}
          <SignInLink>Go to sign in</SignInLink>
        </div>
      </InviteShell>
    );
  }

  if (view.phase === 'registered') {
    return (
      <InviteShell>
        <InviteHeader title="You already have an account" headingRef={headingRef} />
        <div className="accept-invite-message accept-invite-message--warning">
          <p>{view.message}</p>
          <p>Sign in with {view.email} instead.</p>
        </div>
        <div className="accept-invite-actions">
          <SignInLink email={view.email}>Go to sign in</SignInLink>
        </div>
      </InviteShell>
    );
  }

  if (view.phase === 'accepted') {
    return (
      <InviteShell>
        <InviteHeader
          title="Your account is ready"
          subtitle={`You have joined ${view.accepted.organizationName}.`}
          headingRef={headingRef}
        />
        <div className="accept-invite-message accept-invite-message--success">
          <p>Sign in with {view.accepted.email} and the password you just set.</p>
        </div>
        <div className="accept-invite-actions">
          <SignInLink email={view.accepted.email}>Go to sign in</SignInLink>
        </div>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      {/*
        The token is always present in this phase - a preview cannot load without one - but the
        guard keeps that a fact the types check rather than one a reader has to take on trust.
      */}
      {token && (
        <AcceptInviteForm token={token} preview={view.preview} onOutcome={setOutcome} />
      )}
    </InviteShell>
  );
};
